import React, { useRef, useState, useEffect } from "react";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import {
  TextField,
  FormControl,
  FormHelperText,
  CircularProgress,
} from "@material-ui/core";

import styles from "assets/jss/material-kit-react/views/landingPageSections/emailStyle.js";

import * as Fathom from "fathom-client";
import ReCAPTCHA from "react-google-recaptcha";
import { Button } from "@material-ui/core";
import { sendEmail } from "api";
import Heart from "components/Heart";
import addrs from "email-addresses";
import useWindowSize from "useWindowSize";
import Check from "components/Check";
import PropTypes from "prop-types";
import { ShareButtons } from "components/ShareButtons/ShareButtons.js";
import Close from "@material-ui/icons/Close";

// const header = "Hei,";

// const body1 =
//   "Jeg er i likhet med alle andre nordmenn deleier av Oljefondet. Jeg har via kampanjesiden til Fagforbundet og Norsk Folkehjelp fått vite at dere investerer i selskaper som bidrar til brudd på folkeretten og menneskerettigheter på okkupert palestinsk område.";

// const body2 =
//   "Det syns jeg er skuffende, derfor har jeg meldt på dette oppropet for å vise at jeg bryr meg. Jeg ønsker ikke at Oljefondet skal tilrettelegge for plassering av penger i slike selskaper. Jeg ber dere arbeide aktivt med å forbedre praksis på dette området.";

// const regards = "Med vennlig hilsen";

const useStyles = makeStyles(styles);

const EmailSection = ({ english, isOpen, onClose }) => {
  const [code, setCode] = useState(null);
  const [success, setSuccess] = useState(false);
  const [sending, setSending] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);
  const [generalError, setGeneralError] = useState();
  const captchaRef = useRef();
  const { width } = useWindowSize();

  const classes = useStyles();
  const isModal = isOpen !== undefined && onClose !== undefined;

  useEffect(() => {
    const handleClickOutside = (event) => {
      const modal = document.querySelector(`.${classes.card}`);
      const openButton = document.getElementById("openModalButton");

      let targetElement = event.target;
      do {
        if (targetElement === modal || targetElement === openButton) {
          return;
        }
        targetElement = targetElement.parentNode;
      } while (targetElement);

      onClose();
    };

    let clickOutsideListener;
    if (isModal && isOpen) {
      clickOutsideListener = setTimeout(() => {
        window.addEventListener("click", handleClickOutside);
      }, 100);
    }

    return () => {
      clearTimeout(clickOutsideListener);
      window.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen, onClose, classes.card, isModal]);

  const compact = width < 575;

  const languageKey = english ? "en" : "no";
  const onEmailChange = (e) => {
    setEmail(e.currentTarget.value);

    if (emailError) {
      setEmailError(false);
    }
  };

  const onNameChange = (e) => {
    setName(e.currentTarget.value);

    if (nameError) {
      setNameError(false);
    }
  };

  const onCaptchaChange = (code) => {
    setCode(code);

    if (captchaError) {
      setCaptchaError(false);
    }
  };

  const onSendClick = async () => {
    let error = false;

    setGeneralError();

    const n = name.trim();

    if (!n) {
      error = true;
      setNameError(true);
    }

    const parsedEmail = addrs.parseOneAddress(email);

    if (!parsedEmail) {
      error = true;
      setEmailError(true);
    }

    if (!code) {
      error = true;
      setCaptchaError(true);
    }

    if (error) {
      return;
    }

    try {
      setSending(true);
      await sendEmail(name, email, english, code);
      setSuccess(true);
      Fathom.trackGoal("LYXTCRWA", 0);
    } catch (e) {
      console.log("ERROR", e);
      const data = e.response.data;
      captchaRef.current.reset();

      if (Array.isArray(data)) {
        data.forEach((error) => {
          switch (error?.field) {
            case "Name":
              setNameError(true);
              break;
            case "Email":
              setEmailError(true);
              break;
            case "RecaptchaResponse":
              setCaptchaError(true);
              break;
            default:
              setGeneralError(error?.error);
          }
        });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={classes.emailSection}>
      <div className={classes.card}>
        <h2 style={{ marginTop: "0" }} className={classes.titleContainer}>
          <div>
            {english ? "Thank you for caring" : "Takk for at du bryr deg"}
            {isModal ? (
              <span className={classes.heartInTitle}>
                <Heart size="small" />
              </span>
            ) : (
              <span className={classes.heartInTitle}>
                <Heart />
              </span>
            )}
          </div>
          {isModal && (
            <button onClick={onClose} className={classes.closeModalButton}>
              <Close size={32} />
            </button>
          )}
        </h2>
        <h3 className={classes.sectionTitle}>
          {english
            ? "You're sending this message to"
            : "Du sender denne meldingen til"}
        </h3>
        <p>
          {english
            ? "The Norwegian minister of finance, the Norwegian oil fund, the fund’s Council on Ethics, and the Norwegian parliament’s standing committee on finance and economic affairs"
            : "Oljefondet, Finansdepartementet, Etikkrådet og Finanskomiteen."}
        </p>
        <h3 className={classes.sectionTitle}>
          {english
            ? "Your name and email address"
            : "Ditt navn og e-postadresse"}
        </h3>
        <div
          className={
            compact ? classes.intputContainerVertical : classes.inputContainer
          }
        >
          <TextField
            value={name}
            onChange={onNameChange}
            label={english ? "Name" : "Navn"}
            className={classes.marginRight}
            required
            error={nameError}
            helperText={
              (nameError && (english ? "Invalid name" : "Ugyldig navn")) || " "
            }
            autoComplete="name"
            style={{ flex: 1 }}
          />
          <TextField
            value={email}
            onChange={onEmailChange}
            label={english ? "Email Address" : "E-postadresse"}
            required
            error={emailError}
            helperText={
              (emailError &&
                (english
                  ? "Invalid email address"
                  : "Ugyldig e-postadresse")) ||
              " "
            }
            autoComplete="email"
            style={{ flex: 1 }}
          />
        </div>
        <p>
          {english
            ? "Your name and email address will only be visible to the recipients of this email. We'll never share your details with anyone else."
            : "Navnet ditt og din e-postadresse vises kun til Oljefondet, Finansdepartementet, Etikkrådet og Finanskomiteen. Vi deler den ikke med noen andre!"}
        </p>
        <h3 className={classes.sectionTitle}>
          {english
            ? "Your letter to the recipients"
            : "Ditt brev til mottakerene"}
        </h3>
        <p style={{ marginTop: "20px" }}>
          {english ? "Dear Sir/Madam," : "Hei,"}
        </p>
        <p>
          {english
            ? "I have learnt from the oil fund campaign website of the Norwegian Union of Municipal and General Employees and Norwegian People's Aid that the fund is investing in companies that contribute to violations of international law and human rights in the occupied Palestinian territory."
            : "Midlene i Oljefondet er det norske folks sparepenger. Jeg har via kampanjesiden til Fagforbundet og Norsk Folkehjelp fatt vite at dere investerer i selskaper som medvirker til brudd på folkeretten og menneskerettigheter på okkupert palestinsk område."}
        </p>
        <p style={{ marginBottom: "20px" }}>
          {english
            ? "I find this disappointing. I have therefore signed up to this petition to show that I care. I do not want the oil fund to invest in these companies. I ask you to work to improve practice in this area."
            : "Det syns jeg er skuffende. Jeg har derfor meldt meg på dette oppropet for å vise at jeg bryr meg. Jeg ønsker ikke at Oljefondet skal investerer i slike selskaper. Jeg ber dere arbeide med å forbedre praksis på dette området."}
        </p>
        <p className={classes.nameAndEmailContainer}>
          {english ? "Yours faithfully," : "Med vennlig hilsen"}
          <br />
          {name || (english ? "[Your name]" : "[ditt navn]")} <br />
          {email || (english ? "[Your email address]" : "[din e-postadresse]")}
          <br />
        </p>
        <FormControl error={captchaError}>
          {!success && (
            <ReCAPTCHA
              key={languageKey}
              hl={english ? "en" : "no"}
              ref={captchaRef}
              sitekey={process.env.REACT_APP_CAPTCHA_KEY}
              onChange={onCaptchaChange}
              size={compact ? "compact" : "normal"}
            />
          )}
          <FormHelperText>
            {(captchaError &&
              (english
                ? "Confirm that you're not a robot"
                : "Bekreft at du ikke er en robot")) ||
              " "}
          </FormHelperText>
        </FormControl>
        <br />
        <FormControl error={!!generalError || nameError || emailError}>
          <FormHelperText style={{ alignSelf: "center" }}>
            {generalError ||
              (nameError || emailError
                ? english
                  ? "Remember to fill out all the fields "
                  : "Husk å fylle ut alle feltene"
                : " ")}
          </FormHelperText>
          {success ? (
            <div className={classes.sendButtonComplete}>
              <div className={classes.marginRight}>
                {english
                  ? "Thanks for participating!"
                  : "Takk for din deltakelse!"}
              </div>
              <Check />
            </div>
          ) : (
            <>
              <Button
                className={classes.sendButton}
                onClick={onSendClick}
                disabled={sending}
              >
                <div className={classes.marginRight}>
                  {english ? "Send email" : "Send epost"}
                </div>

                <Heart size="small" />

                {sending && (
                  <CircularProgress
                    size="1.3em"
                    className={classes.sendButtonProgress}
                  />
                )}
              </Button>
            </>
          )}
          <div>
            <ShareButtons
              english={english}
              isEmailSection={true}
              width={width}
            />
          </div>
        </FormControl>
      </div>
    </div>
  );
};

EmailSection.propTypes = {
  english: PropTypes.bool,
};

export default EmailSection;
